import React from "react";

import { ReviewChangesPanel } from "./ReviewChangesPanel";
import { useDeferredReady } from "./hooks/useDeferredReady";
import type { SkillCollectionReviewItem } from "./store";

import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  PhasedReviewDialog,
  type PhasedReviewDialogPhase,
} from "@/components/ui/VersionReviewDialog/PhasedReviewDialog";

export type RollbackReviewPhase = PhasedReviewDialogPhase;

interface RollbackReviewDialogProps {
  isOpen: boolean;
  targetVersionNumber: number | null;
  reviewItems: SkillCollectionReviewItem[];
  isLoadingReviewItems: boolean;
  emptyMessage?: string;
  rollbackPhase: RollbackReviewPhase;
  isRestoring: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const RollbackReviewDialog = ({
  isOpen,
  targetVersionNumber,
  reviewItems,
  isLoadingReviewItems,
  emptyMessage,
  rollbackPhase,
  isRestoring,
  onClose,
  onConfirm,
}: RollbackReviewDialogProps) => {
  const isDiffsReady = useDeferredReady(isOpen);

  return (
    <PhasedReviewDialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Rollback to Version ${targetVersionNumber ?? ""}`}
      phase={rollbackPhase}
      statusTitle={
        rollbackPhase === "saving" ? "Rolling back skill collection" : "Version restored"
      }
      statusDescription={
        rollbackPhase === "saving"
          ? `Restoring version ${targetVersionNumber ?? ""} as the new latest version.`
          : "The restored version is ready. Opening it now."
      }
      reviewContent={
        <div className="flex flex-col gap-y-4">
          <ReviewChangesPanel
            reviewItems={reviewItems}
            description={`Review these changes before restoring version ${targetVersionNumber ?? ""} as the new latest version.`}
            isReady={isDiffsReady}
            isLoadingNoticeVisible={isLoadingReviewItems}
            loadingMessage={
              isLoadingReviewItems ? "Loading version snapshots..." : "Preparing review..."
            }
            emptyMessage={emptyMessage}
          />

          <DialogFooter>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isRestoring}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={onConfirm}
                disabled={isLoadingReviewItems || !isDiffsReady}
              >
                Rollback
              </Button>
            </div>
          </DialogFooter>
        </div>
      }
    />
  );
};
